import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Icon, Skeleton, SkeletonForm } from '@repo/ui';
import { Button, Input, Label, Card, CardHeader, CardBody, LastUpdated } from '@repo/ui';
import { FolderCreationTest, ConditionLogicBuilder } from '../components';
import { BankAccountCustomFieldMapping } from '../components/BankAccountCustomFieldMapping';
import { showToast } from '@repo/ui';
import { archivalApi } from '@xtnd/web/features/archival/api/archivalApi';
import type { ArchivalConfiguration } from '@xtnd/web/features/archival/types/archivalType';
import { api } from '@xtnd/web/shared/api';
import { EmailVerificationButton } from '@xtnd/web/features/settings/components';
import { useSaveJuniperConfigMutation } from '../queries';
import { clioIntegrationApi } from '@xtnd/web/features/settings/api/clioIntegrationApi';

interface EmailEntry {
  email: string;
  verificationStatus?: 'not_started' | 'pending_verification' | 'verified' | 'failed';
  lastChecked?: Date;
}

// Conditional Logic Types
interface ConditionRule {
  id: string;
  fieldId: number;
  fieldName: string;
  operator:
    | 'equals'
    | 'not_equals'
    | 'contains'
    | 'not_contains'
    | 'in'
    | 'not_in'
    | 'greater_than'
    | 'less_than';
  value: string | string[] | null;
}

interface ConditionGroup {
  id: string;
  conditions: ConditionRule[];
}

interface ConditionalLogicConfigurations {
  enableJuniperProcessing: ConditionGroup[];
  invertJuniperProcessingLogic?: boolean;
  evergreenTrustProcessing: ConditionGroup[];
}

interface CustomFieldOption {
  id: number;
  name: string;
  field_type: string;
  parent_type: string;
  picklist_options?: Array<{ id: number; option: string }>;
}

interface JuniperConfigData {
  fromEmailAddress: string;
  fromEmailVerified: boolean;
  fromEmailVerificationStatus?: 'not_started' | 'pending_verification' | 'verified' | 'failed';
  replyToEmailAddress?: string;
  invoiceTemplate: Array<{ templateId: string; default: boolean; name: string }>;
  emailTemplateId: string; // Legacy field - kept for backward compatibility, maps to evergreenEmailTemplateId
  evergreenEmailTemplateId?: string;
  operationalEmailTemplateId?: string;
  trustRequestEmailTemplateId?: string;
  emailSubjectLine: string; // Legacy field - kept for backward compatibility, maps to evergreenEmailSubject
  evergreenEmailSubject?: string;
  operationalEmailSubject: string;
  trustRequestEmailSubject: string;
  trustEstablishmentEmailTemplateId?: string;
  trustEstablishmentEmailSubject?: string;
  midCycleRequestEmailTemplateId?: string;
  midCycleRequestEmailSubject?: string;
  // Reminder Email Templates (used for payment reminder follow-ups)
  operationalReminderEmailTemplateId?: string;
  evergreenReminderEmailTemplateId?: string;
  trustRequestReminderEmailTemplateId?: string;
  trustEstablishmentReminderEmailTemplateId?: string;
  midCycleRequestReminderEmailTemplateId?: string;
  emailBcc: string[];
  // Email Override Configuration
  overrideClientEmails: boolean;
  overrideEmailAddress: string;
  // Clio Folder Structure - Fixed Names
  clioMatterRootFolderName: string; // Always "XTND"
  clioMatterReceiptsFolderName: string; // Always "Juniper-Receipts"
  clioMatterInvoicesFolderName: string; // Always "Juniper-Invoices"
  clioMatterReceiptsOutboxFolderName: string; // Always "Receipts-Outbox"
  clioMatterReceiptsFiledFolderName: string; // Always "Receipts-Filed"
  // Clio Folder Structure - Configurable Templates
  clioMatterReceiptsFiledSubfolderTemplate: string; // Template for receipt filed subfolder
  clioMatterInvoicePdfFilenameTemplate: string; // Template for invoice PDF filename
  clioMatterLedgerPdfFilenameTemplate: string; // Template for ledger PDF filename
  // Test Configuration
  clioTestMatterId: string; // Matter ID for testing folder creation
  autoSendEmails: boolean;
  autoApproveTrustRequests: boolean;
  enableTrustRequestRetry: boolean;
  enableTrustRequestVoid: boolean;
  invoiceReportTemplateId: string;
  ledgerReportTemplateId: string;
  trustRequestTemplateId?: string;
  operationalBillTemplateId?: string;
  // Bank and Payment Configuration
  // Legacy mapping — kept for read fallback so existing tenants don't lose
  // their setting on first load post-deploy. New writes go to the trust /
  // operational specific fields below.
  bankAccountCustomFieldMapping?: {
    fieldId?: number;
    fieldName?: string;
    fieldType?: 'text' | 'dropdown' | 'textarea' | 'picklist';
    sampleValues?: string[];
  };
  trustBankAccountCustomFieldMapping?: {
    fieldId?: number;
    fieldName?: string;
    fieldType?: 'text' | 'dropdown' | 'textarea' | 'picklist';
    sampleValues?: string[];
  };
  operationalBankAccountCustomFieldMapping?: {
    fieldId?: number;
    fieldName?: string;
    fieldType?: 'text' | 'dropdown' | 'textarea' | 'picklist';
    sampleValues?: string[];
  };
  defaultOperationalDirectBankAccountId?: string;
  defaultTrustBankAccountId?: string;
  allowPartialPayments?: boolean;
  paymentLinkSubjectMode?: 'bill' | 'contact';
  // Bank account configuration
  archivalServiceId: string;
  enableExternalArchival: boolean;
  archivalFileNamePattern: string;
  archivalFolderName?: string; // Optional archival folder name
  escalationEmails: EmailEntry[];
  slackWebhookPath: string;
  queryCacheTtlMinutes: number;
  // Email Receipts Configuration
  enableEmailReceipts: boolean;
  emailReceiptsConfig: {
    allowedTypes: string[];
    maxTotalSizeBytes: number;
    maxFilesPerEmail: number;
    outboxFolderName: string;
    filedFolderName: string;
    sendingFolderSuffix: string;
    enableValidation: boolean;
    enableCleanup: boolean;
  };
  // Ledger Report Configuration
  includeLedgerInEmail: boolean;
  // Trust Request PDF Upload Configuration
  uploadTrustRequestPdfsToClio: boolean;
  // Payment Reminder Revision Upload Configuration
  uploadPaymentReminderRevisionsToClio: boolean;
  // Conditional Logic Configuration
  conditionalLogicConfigurations?: ConditionalLogicConfigurations;
  // Legacy Bill Date Filter Configuration
  legacyBillCutoffDate?: string; // ISO date string (YYYY-MM-DD)
  // Trust Request Note Templates
  operationalTrustRequestNoteTemplate: string;
  bulkTrustRequestNoteTemplate: string;
  // Custom Field Filters Configuration
  customFieldFilters: Array<{
    fieldId: number;
    fieldName: string;
    fieldType: 'picklist' | 'dropdown' | 'checkbox';
    parentType: 'Matter';
    enabled: boolean;
  }>;
}

interface Template {
  id: string;
  name: string;
  type: 'pdf' | 'email';
  description?: string;
}

// Email validation utility
const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export function JuniperConfigPage() {
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);

  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const [config, setConfig] = useState<JuniperConfigData>({
    fromEmailAddress: '',
    fromEmailVerified: false,
    fromEmailVerificationStatus: 'not_started',
    replyToEmailAddress: '',
    invoiceTemplate: [],
    emailTemplateId: '', // Legacy - will be synced with evergreenEmailTemplateId
    evergreenEmailTemplateId: '',
    operationalEmailTemplateId: '',
    trustRequestEmailTemplateId: '',
    trustEstablishmentEmailTemplateId: '',
    midCycleRequestEmailTemplateId: '',
    emailSubjectLine: 'Your Juniper Transaction #{juniperId}', // Legacy - will be synced with evergreenEmailSubject
    evergreenEmailSubject: 'Your Juniper Transaction #{juniperId}',
    operationalEmailSubject: 'Operational Invoice - #{operationalBillNumber}',
    trustRequestEmailSubject: 'Trust Replenishment Request - #{trustRequestNumber}',
    trustEstablishmentEmailSubject: 'Trust Establishment Request - #{trustRequestNumber}',
    midCycleRequestEmailSubject: 'Mid-Cycle Trust Request - #{trustRequestNumber}',
    emailBcc: [],
    // Email Override Configuration
    overrideClientEmails: false,
    overrideEmailAddress: '',
    // Clio Folder Structure - Fixed Names (lowercase to match ensureJuniperFolderStructure)
    clioMatterRootFolderName: 'xtnd',
    clioMatterReceiptsFolderName: 'juniper-receipts',
    clioMatterInvoicesFolderName: 'juniper-invoices',
    clioMatterReceiptsOutboxFolderName: 'juniper-receipt-outbox',
    clioMatterReceiptsFiledFolderName: 'juniper-receipt-filed',
    // Clio Folder Structure - Configurable Templates
    clioMatterReceiptsFiledSubfolderTemplate:
      '{juniperId}-{trustRequestNumber}-{operationalInvoiceNumber}',
    clioMatterInvoicePdfFilenameTemplate:
      '{juniperId}-{trustRequestNumber}-{operationalInvoiceNumber}-Invoice.pdf',
    clioMatterLedgerPdfFilenameTemplate:
      '{juniperId}-{trustRequestNumber}-{operationalInvoiceNumber}-Ledger.pdf',
    // Test Configuration
    clioTestMatterId: '',
    autoSendEmails: true,
    autoApproveTrustRequests: false,
    enableTrustRequestRetry: true,
    enableTrustRequestVoid: true,
    invoiceReportTemplateId: 'template-standard',
    ledgerReportTemplateId: '',
    trustRequestTemplateId: '',
    operationalBillTemplateId: '',
    archivalServiceId: '',
    enableExternalArchival: false,
    archivalFileNamePattern: '{juniperId}-juniper-data-{timestamp}.json',
    escalationEmails: [{ email: 'ops@xtnd.com', verificationStatus: 'not_started' }],
    slackWebhookPath: '/xtnd/slack-webhook',
    queryCacheTtlMinutes: 15,
    // Email Receipts Configuration
    enableEmailReceipts: false,
    emailReceiptsConfig: {
      allowedTypes: ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'txt'],
      maxTotalSizeBytes: 25 * 1024 * 1024, // 25MB
      maxFilesPerEmail: 10,
      outboxFolderName: 'juniper-receipt-outbox',
      filedFolderName: 'juniper-receipt-filed',
      sendingFolderSuffix: '_sending',
      enableValidation: true,
      enableCleanup: true,
    },
    // Bank Account Mapping
    bankAccountCustomFieldMapping: undefined,
    trustBankAccountCustomFieldMapping: undefined,
    operationalBankAccountCustomFieldMapping: undefined,
    defaultOperationalDirectBankAccountId: '',
    // Ledger Report Configuration
    includeLedgerInEmail: true,
    // Trust Request PDF Upload Configuration (disabled by default)
    uploadTrustRequestPdfsToClio: false,
    // Payment reminder revision upload configuration (disabled by default)
    uploadPaymentReminderRevisionsToClio: false,
    // Bank account configuration
    defaultTrustBankAccountId: undefined,
    // Conditional Logic Configuration
    conditionalLogicConfigurations: {
      enableJuniperProcessing: [],
      invertJuniperProcessingLogic: false,
      evergreenTrustProcessing: [],
    },
    // Legacy Bill Date Filter Configuration
    legacyBillCutoffDate: undefined,
    // Trust Request Note Templates
    operationalTrustRequestNoteTemplate:
      '{{operationalMemo}}Generated by XTND-Juniper for operational invoice {{operationalInvoiceNumber}}.',
    bulkTrustRequestNoteTemplate: '{{userNotes}}Bulk trust request.',
    // Custom Field Filters Configuration
    customFieldFilters: [],
  });

  // Fetch current configuration
  const { data: currentConfig, isLoading } = useQuery<JuniperConfigData>({
    queryKey: ['juniper-config'],
    queryFn: async () => {
      // Use the JWT-based endpoint that extracts tenant ID from the token
      const response = await api.get('/juniper/config');
      return response as JuniperConfigData;
    },
    retry: 3,
    retryDelay: 1000,
  });

  // Fetch available templates
  const { data: templates = [] } = useQuery<Template[]>({
    queryKey: ['templates'],
    queryFn: async () => {
      // Use JWT-based endpoint that extracts tenant ID from token
      const data = (await api.get('/templates/config')) as any;
      const allTemplates: Template[] = [];

      // Handle the nested object structure returned by the API
      if (data.templates) {
        // Process PDF templates
        if (data.templates.pdf && typeof data.templates.pdf === 'object') {
          Object.entries(data.templates.pdf).forEach(([templateId, template]: [string, any]) => {
            allTemplates.push({
              id: templateId,
              name: template.templateName || template.name || templateId,
              type: 'pdf',
              description: template.description || template.templateName || template.name,
            });
          });
        }

        // Process Email templates
        if (data.templates.email && typeof data.templates.email === 'object') {
          Object.entries(data.templates.email).forEach(([templateId, template]: [string, any]) => {
            allTemplates.push({
              id: templateId,
              name: template.templateName || template.name || templateId,
              type: 'email',
              description: template.description || template.templateName || template.name,
            });
          });
        }
      }

      return allTemplates;
    },
  });

  // Fetch archival configurations
  const { data: archivalConfigs = [] } = useQuery<ArchivalConfiguration[]>({
    queryKey: ['archival-configs'],
    queryFn: async () => {
      // Use JWT-based endpoint that extracts tenant ID from token
      const response = await archivalApi.getConfigurations();
      return response;
    },
  });

  // Fetch custom fields for conditional logic
  const { data: customFields = [] } = useQuery<CustomFieldOption[]>({
    queryKey: ['clio-custom-fields'],
    queryFn: async () => {
      const response = await api.get('/clio/custom-fields');
      return (response as any)?.data || [];
    },
    retry: 2,
    retryDelay: 1000,
  });

  // Update configuration mutation
  const saveMutation = useSaveJuniperConfigMutation();

  // Initialize form with current config when loaded
  useEffect(() => {
    if (currentConfig) {
      setConfig(prev => {
        const updatedConfig = {
          ...prev,
          ...currentConfig,
          // Sync legacy fields with new fields for backward compatibility
          evergreenEmailTemplateId:
            currentConfig.evergreenEmailTemplateId || currentConfig.emailTemplateId || '',
          operationalEmailTemplateId: currentConfig.operationalEmailTemplateId || '',
          trustRequestEmailTemplateId: currentConfig.trustRequestEmailTemplateId || '',
          trustRequestTemplateId: currentConfig.trustRequestTemplateId || '',
          operationalBillTemplateId: currentConfig.operationalBillTemplateId || '',
          evergreenEmailSubject:
            currentConfig.evergreenEmailSubject ||
            currentConfig.emailSubjectLine ||
            'Your Juniper Transaction #{juniperId}',
          // Normalize escalationEmails to EmailEntry[]
          escalationEmails: (currentConfig.escalationEmails || []).map((entry: any) => {
            if (typeof entry === 'string') {
              return { email: entry, verificationStatus: 'not_started' } as EmailEntry;
            }
            if (entry && typeof entry === 'object') {
              return {
                email: entry.email || '',
                verificationStatus: entry.verificationStatus,
                lastChecked: entry.lastChecked ? new Date(entry.lastChecked) : undefined,
              } as EmailEntry;
            }
            return { email: '', verificationStatus: 'not_started' } as EmailEntry;
          }),
        };

        // Explicitly handle legacy bill cutoff date
        if (Object.prototype.hasOwnProperty.call(currentConfig, 'legacyBillCutoffDate')) {
          updatedConfig.legacyBillCutoffDate = currentConfig.legacyBillCutoffDate;
        }

        return updatedConfig;
      });
    }
  }, [currentConfig]);

  // Validation function
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Required fields
    if (!config.fromEmailAddress.trim()) {
      errors.fromEmailAddress = 'From Email Address is required';
    } else if (!isValidEmail(config.fromEmailAddress)) {
      errors.fromEmailAddress = 'Please enter a valid email address';
    }

    // Validate email subjects
    if (!config.evergreenEmailSubject?.trim() && !config.emailSubjectLine.trim()) {
      errors.evergreenEmailSubject = 'Evergreen Email Subject is required';
    }
    if (!config.operationalEmailSubject.trim()) {
      errors.operationalEmailSubject = 'Operational Direct Email Subject is required';
    }
    if (!config.trustRequestEmailSubject.trim()) {
      errors.trustRequestEmailSubject = 'Trust Request Email Subject is required';
    }

    // Validate email templates
    if (!config.evergreenEmailTemplateId && !config.emailTemplateId) {
      errors.evergreenEmailTemplateId = 'Evergreen Email Template is required';
    }
    if (!config.operationalEmailTemplateId) {
      errors.operationalEmailTemplateId = 'Operational Direct Email Template is required';
    }
    if (!config.trustRequestEmailTemplateId) {
      errors.trustRequestEmailTemplateId = 'Trust Request Email Template is required';
    }
    if (!config.trustEstablishmentEmailTemplateId) {
      errors.trustEstablishmentEmailTemplateId = 'Trust Establishment Email Template is required';
    }
    if (!config.midCycleRequestEmailTemplateId) {
      errors.midCycleRequestEmailTemplateId = 'Mid-Cycle Request Email Template is required';
    }
    if (!config.operationalBillTemplateId) {
      errors.operationalBillTemplateId = 'Operational Invoice Template is required';
    }
    if (!config.trustRequestTemplateId) {
      errors.trustRequestTemplateId = 'Trust Request Template is required';
    }

    // Validate email subjects
    if (!config.evergreenEmailSubject && !config.emailSubjectLine) {
      errors.evergreenEmailSubject = 'Evergreen Email Subject is required';
    }
    if (!config.operationalEmailSubject) {
      errors.operationalEmailSubject = 'Operational Email Subject is required';
    }
    if (!config.trustRequestEmailSubject) {
      errors.trustRequestEmailSubject = 'Trust Request Email Subject is required';
    }
    if (!config.trustEstablishmentEmailSubject) {
      errors.trustEstablishmentEmailSubject = 'Trust Establishment Email Subject is required';
    }
    if (!config.midCycleRequestEmailSubject) {
      errors.midCycleRequestEmailSubject = 'Mid-Cycle Request Email Subject is required';
    }

    // Clio Integration required fields - configurable templates only
    if (!config.clioMatterReceiptsFiledSubfolderTemplate.trim()) {
      errors.clioMatterReceiptsFiledSubfolderTemplate =
        'Receipt Filed Subfolder Template is required';
    }

    if (!config.clioMatterInvoicePdfFilenameTemplate.trim()) {
      errors.clioMatterInvoicePdfFilenameTemplate = 'Invoice PDF Filename Template is required';
    }

    if (!config.clioMatterLedgerPdfFilenameTemplate.trim()) {
      errors.clioMatterLedgerPdfFilenameTemplate = 'Ledger PDF Filename Template is required';
    }

    // BCC Email validation
    config.emailBcc.forEach((email, index) => {
      if (email.trim() && !isValidEmail(email)) {
        errors[`emailBcc_${index}`] = 'Please enter a valid email address';
      }
    });

    // Escalation Email validation (handle string or object)
    config.escalationEmails.forEach((emailEntry: any, index) => {
      const emailValue = typeof emailEntry === 'string' ? emailEntry : emailEntry?.email || '';
      if (emailValue.trim() && !isValidEmail(emailValue)) {
        errors[`escalationEmail[${index}]`] = 'Please enter a valid email address';
      }
    });

    // Override Email validation
    if (config.overrideClientEmails) {
      if (!config.overrideEmailAddress.trim()) {
        errors.overrideEmailAddress = 'Override Email Address is required when override is enabled';
      } else if (!isValidEmail(config.overrideEmailAddress)) {
        errors.overrideEmailAddress = 'Please enter a valid email address';
      }
    }

    // Reply-To Email validation
    if (config.replyToEmailAddress?.trim()) {
      if (!isValidEmail(config.replyToEmailAddress)) {
        errors.replyToEmailAddress = 'Please enter a valid email address';
      }
    }

    // Legacy Bill Cutoff Date validation
    if (config.legacyBillCutoffDate) {
      const cutoffDate = new Date(config.legacyBillCutoffDate);
      if (isNaN(cutoffDate.getTime())) {
        errors.legacyBillCutoffDate = 'Please enter a valid date';
      } else if (cutoffDate > new Date()) {
        errors.legacyBillCutoffDate = 'Cutoff date cannot be in the future';
      }
    }

    // Conditional Logic validation
    if (config.conditionalLogicConfigurations) {
      const { enableJuniperProcessing, evergreenTrustProcessing } =
        config.conditionalLogicConfigurations;

      // Validate Enable Juniper Processing groups
      enableJuniperProcessing?.forEach((group, groupIndex) => {
        if (!group.conditions || group.conditions.length === 0) {
          errors[`enableJuniperProcessing_group_${groupIndex}`] =
            'Each condition group must have at least one condition';
        }

        group.conditions?.forEach((condition, condIndex) => {
          if (!condition.fieldId) {
            errors[`enableJuniperProcessing_group_${groupIndex}_cond_${condIndex}_field`] =
              'Please select a custom field';
          }
          // Allow null values for equals/not_equals operators (NULL checks). A
          // condition reloaded from the saved config can carry `undefined`
          // instead of `null` for an intentionally-empty value (the key is
          // simply absent), so both must be treated as "missing" here —
          // same normalization ConditionRuleBuilder's display logic and the
          // backend's conditional-logic-evaluation.service already use.
          const supportsNull =
            condition.operator === 'equals' || condition.operator === 'not_equals';
          const isMissingValue = condition.value === null || condition.value === undefined;
          if (isMissingValue && !supportsNull) {
            errors[`enableJuniperProcessing_group_${groupIndex}_cond_${condIndex}_value`] =
              'Please enter a value';
          } else if (
            !isMissingValue &&
            (!condition.value || (Array.isArray(condition.value) && condition.value.length === 0))
          ) {
            errors[`enableJuniperProcessing_group_${groupIndex}_cond_${condIndex}_value`] =
              'Please enter a value';
          }
        });
      });

      // Validate Evergreen Trust Processing groups
      evergreenTrustProcessing?.forEach((group, groupIndex) => {
        if (!group.conditions || group.conditions.length === 0) {
          errors[`evergreenTrustProcessing_group_${groupIndex}`] =
            'Each condition group must have at least one condition';
        }

        group.conditions?.forEach((condition, condIndex) => {
          if (!condition.fieldId) {
            errors[`evergreenTrustProcessing_group_${groupIndex}_cond_${condIndex}_field`] =
              'Please select a custom field';
          }
          // Allow null values for equals/not_equals operators (NULL checks). A
          // condition reloaded from the saved config can carry `undefined`
          // instead of `null` for an intentionally-empty value (the key is
          // simply absent), so both must be treated as "missing" here —
          // same normalization ConditionRuleBuilder's display logic and the
          // backend's conditional-logic-evaluation.service already use.
          const supportsNull =
            condition.operator === 'equals' || condition.operator === 'not_equals';
          const isMissingValue = condition.value === null || condition.value === undefined;
          if (isMissingValue && !supportsNull) {
            errors[`evergreenTrustProcessing_group_${groupIndex}_cond_${condIndex}_value`] =
              'Please enter a value';
          } else if (
            !isMissingValue &&
            (!condition.value || (Array.isArray(condition.value) && condition.value.length === 0))
          ) {
            errors[`evergreenTrustProcessing_group_${groupIndex}_cond_${condIndex}_value`] =
              'Please enter a value';
          }
        });
      });
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      showToast.error('Please fix validation errors before saving');
      return;
    }

    setIsSaving(true);
    try {
      // Sync legacy fields before saving for backward compatibility
      const configToSave = {
        ...config,
        // Ensure legacy fields are synced with new fields
        emailTemplateId: config.evergreenEmailTemplateId || config.emailTemplateId,
        emailSubjectLine: config.evergreenEmailSubject || config.emailSubjectLine,
      };
      await saveMutation.mutateAsync(configToSave);
    } finally {
      setIsSaving(false);
    }
  };

  const handleFromEmailChange = (email: string) => {
    setConfig(prev => ({
      ...prev,
      fromEmailAddress: email,
      // Remove verified status if email changed
      fromEmailVerified: email === prev.fromEmailAddress ? prev.fromEmailVerified : false,
    }));
  };

  const handleTestFolderCreation = async (matterId: string) => {
    try {
      const result = (await api.post('/juniper/test-folder-creation', {
        matterId: parseInt(matterId),
        config: {
          rootFolderName: config.clioMatterRootFolderName,
          receiptsFolderName: config.clioMatterReceiptsFolderName,
          invoicesFolderName: config.clioMatterInvoicesFolderName,
          receiptsOutboxFolderName: config.clioMatterReceiptsOutboxFolderName,
          receiptsFiledFolderName: config.clioMatterReceiptsFiledFolderName,
          receiptsFiledSubfolderTemplate: config.clioMatterReceiptsFiledSubfolderTemplate,
          invoicePdfFilenameTemplate: config.clioMatterInvoicePdfFilenameTemplate,
          ledgerPdfFilenameTemplate: config.clioMatterLedgerPdfFilenameTemplate,
        },
      })) as any;

      return {
        success: true,
        matterId,
        xtndFolderId: result.folderStructure.xtndFolderId,
        testTransactionFolderName: result.testTransactionFolderName,
      };
    } catch (error: any) {
      return {
        success: false,
        matterId,
        error: error.message,
      };
    }
  };

  const addBccEmail = () => {
    setConfig(prev => ({
      ...prev,
      emailBcc: [...prev.emailBcc, ''],
    }));
  };

  const updateBccEmail = (index: number, value: string) => {
    setConfig(prev => ({
      ...prev,
      emailBcc: prev.emailBcc.map((email, i) => (i === index ? value : email)),
    }));
  };

  const removeBccEmail = (index: number) => {
    setConfig(prev => ({
      ...prev,
      emailBcc: prev.emailBcc.filter((_, i) => i !== index),
    }));
  };

  const handleAddEscalationEmail = () => {
    setConfig(prev => ({
      ...prev,
      escalationEmails: [
        ...prev.escalationEmails,
        { email: '', verificationStatus: 'not_started' },
      ],
    }));
  };

  const handleUpdateEscalationEmail = (index: number, value: string) => {
    setConfig(prev => ({
      ...prev,
      escalationEmails: prev.escalationEmails.map((emailEntry, i) =>
        i === index
          ? {
              ...emailEntry,
              email: value,
              verificationStatus:
                emailEntry.email !== value ? 'not_started' : emailEntry.verificationStatus,
            }
          : emailEntry
      ),
    }));
  };

  const handleRemoveEscalationEmail = (index: number) => {
    setConfig(prev => ({
      ...prev,
      escalationEmails: prev.escalationEmails.filter((_, i) => i !== index),
    }));
  };

  // Filter templates - ensure only PDFs for PDF dropdowns
  const pdfTemplates = templates.filter(t => t.type === 'pdf');
  const emailTemplates = templates.filter(t => t.type === 'email');
  const archivalServices = archivalConfigs?.filter((ac: ArchivalConfiguration) => ac.enabled) || [];

  // Check if configuration is complete for "Configured" status
  const isConfigured = !!(
    config.fromEmailAddress &&
    config.fromEmailVerified &&
    config.emailSubjectLine &&
    config.emailTemplateId &&
    config.clioMatterReceiptsFiledSubfolderTemplate &&
    config.clioMatterInvoicePdfFilenameTemplate &&
    config.clioMatterLedgerPdfFilenameTemplate
  );

  if (isLoading) {
    return (
      <div className="space-y-6">
        {/* Header Skeleton */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Skeleton variant="circular" width={32} height={32} />
            <Skeleton variant="text" width="300px" height="2rem" />
          </div>
          <Skeleton variant="rectangular" width="120px" height="40px" />
        </div>

        {/* Form Skeleton */}
        <Card variant="default">
          <CardBody>
            <SkeletonForm fields={5} />
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-2">
            <div
              className={`w-3 h-3 rounded-full ${isConfigured ? 'bg-green-500' : 'bg-yellow-500'}`}
            />
            <span className="text-sm font-medium text-xtnd-dark-600 dark:text-xtnd-dark-200">
              {isConfigured ? 'Configured' : 'Configuration Incomplete'}
            </span>
          </div>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={isSaving}
            className="bg-xtnd-blue hover:bg-xtnd-blue/90 text-white"
          >
            {isSaving ? 'Saving...' : 'Save Configuration'}
          </Button>
        </div>
      </div>

      <div className="flex items-start justify-between">
        <div className="flex items-center space-x-3">
          <Icon name="HiCog" className="h-8 w-8 text-xtnd-blue" />
          <div>
            <h1 className="text-3xl font-bold text-xtnd-dark dark:text-xtnd-white">
              Juniper Configuration
            </h1>
            <p className="text-xtnd-dark-500 dark:text-xtnd-dark-200 mt-1">
              Configure trust request processing, email notifications, and integration settings
            </p>
          </div>
        </div>
        <LastUpdated className="mt-1" timestamp={(currentConfig as any)?.updatedAt} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Email & Template Configuration */}
        {/* Email & Template Configuration */}
        <Card>
          <CardHeader>
            <h2 className="text-xl font-semibold text-xtnd-dark dark:text-xtnd-white">
              Email & Template Configuration
            </h2>
          </CardHeader>
          <CardBody className="space-y-4">
            <div>
              <Label htmlFor="fromEmail">From Email Address *</Label>
              <div className="flex items-center space-x-2">
                <Input
                  id="fromEmail"
                  type="email"
                  autoComplete="email"
                  value={config.fromEmailAddress}
                  onChange={e => handleFromEmailChange(e.target.value)}
                  placeholder="juniper@yourfirm.com"
                  className={`flex-1 ${validationErrors.fromEmailAddress ? 'border-red-500' : ''}`}
                  required
                />
                <EmailVerificationButton
                  email={config.fromEmailAddress}
                  verificationStatus={config.fromEmailVerificationStatus}
                  onStatusChange={(_, status) => {
                    setConfig(prev => ({
                      ...prev,
                      fromEmailVerificationStatus: status,
                      fromEmailVerified: status === 'verified',
                    }));
                  }}
                />
              </div>
              {validationErrors.fromEmailAddress && (
                <p className="text-sm text-red-600 mt-1">{validationErrors.fromEmailAddress}</p>
              )}
              {config.fromEmailVerificationStatus === 'verified' && (
                <p className="text-sm text-green-600 mt-1">✅ Verified - Can receive emails</p>
              )}
              {config.fromEmailVerificationStatus === 'pending_verification' && (
                <p className="text-sm text-yellow-600 mt-1">
                  ⏳ Pending - Check your email for verification link
                </p>
              )}
              {config.fromEmailVerificationStatus === 'failed' && (
                <p className="text-sm text-red-600 mt-1">❌ Verification failed - Try again</p>
              )}
            </div>

            <div>
              <Label htmlFor="replyToEmail">Reply-To Email Address</Label>
              <Input
                id="replyToEmail"
                type="email"
                autoComplete="email"
                value={config.replyToEmailAddress || ''}
                onChange={e =>
                  setConfig(prev => ({ ...prev, replyToEmailAddress: e.target.value }))
                }
                placeholder="support@yourfirm.com"
                className={validationErrors.replyToEmailAddress ? 'border-red-500' : ''}
              />
              <p className="text-xs text-xtnd-dark-400 dark:text-xtnd-dark-300 mt-1">
                Optional: Alternative email address for customer replies on Juniper emails. If not
                set, replies will go to the From Email Address above.
              </p>
              {validationErrors.replyToEmailAddress && (
                <p className="text-sm text-red-600 mt-1">{validationErrors.replyToEmailAddress}</p>
              )}
            </div>

            {/* Email Templates Section */}
            <div className="space-y-4">
              <Label>Juniper Template Configuration</Label>

              {/* Email Templates Table */}
              <p className="text-xs text-xtnd-dark-500 dark:text-xtnd-dark-300">
                Scroll horizontally on smaller screens to review full template names and subjects.
              </p>
              <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
                <table className="min-w-[132rem] table-auto divide-y divide-xtnd-dark-100 dark:divide-xtnd-dark-600">
                  <thead className="bg-xtnd-dark-50 dark:bg-xtnd-dark-700">
                    <tr>
                      <th className="sticky left-0 z-10 bg-xtnd-dark-50 dark:bg-xtnd-dark-700 px-6 py-3 text-left text-xs font-medium text-xtnd-dark-400 dark:text-xtnd-dark-300 uppercase tracking-wider min-w-[18rem]">
                        Email Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-xtnd-dark-400 dark:text-xtnd-dark-300 uppercase tracking-wider min-w-[23rem]">
                        Email Template
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-xtnd-dark-400 dark:text-xtnd-dark-300 uppercase tracking-wider min-w-[23rem]">
                        Reminder Template
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-xtnd-dark-400 dark:text-xtnd-dark-300 uppercase tracking-wider min-w-[22rem]">
                        Subject Line
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-xtnd-dark-400 dark:text-xtnd-dark-300 uppercase tracking-wider min-w-[23rem]">
                        Invoice Template
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-xtnd-dark-400 dark:text-xtnd-dark-300 uppercase tracking-wider min-w-[23rem]">
                        Ledger Template
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-xtnd-dark-800 divide-y divide-xtnd-dark-100 dark:divide-xtnd-dark-600">
                    {/* OPERATIONAL INVOICES Section */}
                    <tr className="bg-xtnd-dark-50 dark:bg-xtnd-dark-600">
                      <td
                        colSpan={6}
                        className="px-6 py-3 text-left text-xs font-bold text-xtnd-dark-600 dark:text-xtnd-dark-200 uppercase tracking-wider border-b-2 border-xtnd-dark-200 dark:border-xtnd-dark-400"
                      >
                        OPERATIONAL INVOICES
                      </td>
                    </tr>
                    {/* Operational */}
                    <tr>
                      <td className="sticky left-0 z-[1] bg-white dark:bg-xtnd-dark-800 px-6 py-4 whitespace-nowrap text-sm font-medium text-xtnd-dark-800 dark:text-xtnd-dark-50">
                        Operational
                        <p className="text-xs text-xtnd-dark-400 dark:text-xtnd-dark-300 mt-1">
                          For operational invoices
                        </p>
                      </td>
                      <td className="px-6 py-4 align-top">
                        <select
                          value={config.operationalEmailTemplateId || ''}
                          onChange={e =>
                            setConfig(prev => ({
                              ...prev,
                              operationalEmailTemplateId: e.target.value,
                            }))
                          }
                          className={`w-full min-w-[22rem] px-3 py-2 text-sm border border-xtnd-dark-200 dark:border-xtnd-dark-500 rounded-md focus:outline-none focus:ring-2 focus:ring-xtnd-blue bg-white dark:bg-xtnd-dark-700 text-xtnd-dark dark:text-xtnd-white ${validationErrors.operationalEmailTemplateId ? 'border-red-500' : ''}`}
                          required
                        >
                          <option value="">Select template...</option>
                          {emailTemplates.map(template => (
                            <option key={template.id} value={template.id}>
                              {template.name}
                            </option>
                          ))}
                        </select>
                        {validationErrors.operationalEmailTemplateId && (
                          <p className="text-xs text-red-600 mt-1">
                            {validationErrors.operationalEmailTemplateId}
                          </p>
                        )}
                      </td>
                      <td className="px-6 py-4 align-top">
                        <select
                          value={config.operationalReminderEmailTemplateId || ''}
                          onChange={e =>
                            setConfig(prev => ({
                              ...prev,
                              operationalReminderEmailTemplateId: e.target.value,
                            }))
                          }
                          className="w-full min-w-[22rem] px-3 py-2 text-sm border border-xtnd-dark-200 dark:border-xtnd-dark-500 rounded-md focus:outline-none focus:ring-2 focus:ring-xtnd-blue bg-white dark:bg-xtnd-dark-700 text-xtnd-dark dark:text-xtnd-white"
                        >
                          <option value="">Select template...</option>
                          {emailTemplates.map(template => (
                            <option key={template.id} value={template.id}>
                              {template.name}
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-xtnd-dark-400 dark:text-xtnd-dark-300 mt-1">
                          Optional: Used for payment reminder follow-ups
                        </p>
                      </td>
                      <td className="px-6 py-4">
                        <Input
                          value={config.operationalEmailSubject}
                          onChange={e =>
                            setConfig(prev => ({
                              ...prev,
                              operationalEmailSubject: e.target.value,
                            }))
                          }
                          placeholder="Operational Invoice - #{operationalBillNumber}"
                          className={`w-full ${validationErrors.operationalEmailSubject ? 'border-red-500' : ''}`}
                          required
                        />
                        {validationErrors.operationalEmailSubject && (
                          <p className="text-xs text-red-600 mt-1">
                            {validationErrors.operationalEmailSubject}
                          </p>
                        )}
                        <p className="text-xs text-xtnd-dark-400 dark:text-xtnd-dark-300 mt-1">
                          Variables: {'{'}operationalBillNumber{'}'}
                        </p>
                      </td>
                      <td className="px-6 py-4 align-top">
                        <select
                          value={config.operationalBillTemplateId || ''}
                          onChange={e =>
                            setConfig(prev => ({
                              ...prev,
                              operationalBillTemplateId: e.target.value,
                            }))
                          }
                          className={`w-full min-w-[22rem] px-3 py-2 text-sm border border-xtnd-dark-200 dark:border-xtnd-dark-500 rounded-md focus:outline-none focus:ring-2 focus:ring-xtnd-blue bg-white dark:bg-xtnd-dark-700 text-xtnd-dark dark:text-xtnd-white ${validationErrors.operationalBillTemplateId ? 'border-red-500' : ''}`}
                          required
                        >
                          <option value="">Select template...</option>
                          {pdfTemplates.map(template => (
                            <option key={template.id} value={template.id}>
                              {template.name}
                            </option>
                          ))}
                        </select>
                        {validationErrors.operationalBillTemplateId && (
                          <p className="text-xs text-red-600 mt-1">
                            {validationErrors.operationalBillTemplateId}
                          </p>
                        )}
                      </td>
                      <td className="px-6 py-4 align-top">
                        <select
                          value={config.ledgerReportTemplateId || ''}
                          onChange={e =>
                            setConfig(prev => ({ ...prev, ledgerReportTemplateId: e.target.value }))
                          }
                          className="w-full min-w-[22rem] px-3 py-2 text-sm border border-xtnd-dark-200 dark:border-xtnd-dark-500 rounded-md focus:outline-none focus:ring-2 focus:ring-xtnd-blue bg-white dark:bg-xtnd-dark-700 text-xtnd-dark dark:text-xtnd-white"
                        >
                          <option value="">Select ledger template...</option>
                          {pdfTemplates.map(template => (
                            <option key={template.id} value={template.id}>
                              {template.name}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>

                    {/* Evergreen */}
                    <tr className="bg-xtnd-dark-50 dark:bg-xtnd-dark-700">
                      <td className="sticky left-0 z-[1] bg-xtnd-dark-50 dark:bg-xtnd-dark-700 px-6 py-4 whitespace-nowrap text-sm font-medium text-xtnd-dark-800 dark:text-xtnd-dark-50">
                        Evergreen
                        <p className="text-xs text-xtnd-dark-400 dark:text-xtnd-dark-300 mt-1">
                          For evergreen retainer invoices
                        </p>
                      </td>
                      <td className="px-6 py-4 align-top">
                        <select
                          value={config.evergreenEmailTemplateId || config.emailTemplateId || ''}
                          onChange={e =>
                            setConfig(prev => ({
                              ...prev,
                              evergreenEmailTemplateId: e.target.value,
                              emailTemplateId: e.target.value, // Sync legacy field
                            }))
                          }
                          className={`w-full min-w-[22rem] px-3 py-2 text-sm border border-xtnd-dark-200 dark:border-xtnd-dark-500 rounded-md focus:outline-none focus:ring-2 focus:ring-xtnd-blue bg-white dark:bg-xtnd-dark-700 text-xtnd-dark dark:text-xtnd-white ${validationErrors.evergreenEmailTemplateId ? 'border-red-500' : ''}`}
                          required
                        >
                          <option value="">Select template...</option>
                          {emailTemplates.map(template => (
                            <option key={template.id} value={template.id}>
                              {template.name}
                            </option>
                          ))}
                        </select>
                        {validationErrors.evergreenEmailTemplateId && (
                          <p className="text-xs text-red-600 mt-1">
                            {validationErrors.evergreenEmailTemplateId}
                          </p>
                        )}
                      </td>
                      <td className="px-6 py-4 align-top">
                        <select
                          value={config.evergreenReminderEmailTemplateId || ''}
                          onChange={e =>
                            setConfig(prev => ({
                              ...prev,
                              evergreenReminderEmailTemplateId: e.target.value,
                            }))
                          }
                          className="w-full min-w-[22rem] px-3 py-2 text-sm border border-xtnd-dark-200 dark:border-xtnd-dark-500 rounded-md focus:outline-none focus:ring-2 focus:ring-xtnd-blue bg-white dark:bg-xtnd-dark-700 text-xtnd-dark dark:text-xtnd-white"
                        >
                          <option value="">Select template...</option>
                          {emailTemplates.map(template => (
                            <option key={template.id} value={template.id}>
                              {template.name}
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-xtnd-dark-400 dark:text-xtnd-dark-300 mt-1">
                          Optional: Used for payment reminder follow-ups
                        </p>
                      </td>
                      <td className="px-6 py-4">
                        <Input
                          value={config.evergreenEmailSubject || config.emailSubjectLine || ''}
                          onChange={e =>
                            setConfig(prev => ({
                              ...prev,
                              evergreenEmailSubject: e.target.value,
                              emailSubjectLine: e.target.value, // Sync legacy field
                            }))
                          }
                          placeholder="Your Juniper Transaction #{juniperId}"
                          className={`w-full ${validationErrors.evergreenEmailSubject ? 'border-red-500' : ''}`}
                          required
                        />
                        {validationErrors.evergreenEmailSubject && (
                          <p className="text-xs text-red-600 mt-1">
                            {validationErrors.evergreenEmailSubject}
                          </p>
                        )}
                        <p className="text-xs text-xtnd-dark-400 dark:text-xtnd-dark-300 mt-1">
                          Variables: {'{'}trustRequestNumber{'}'}
                        </p>
                      </td>
                      <td className="px-6 py-4 align-top">
                        <select
                          value={config.invoiceReportTemplateId || ''}
                          onChange={e =>
                            setConfig(prev => ({
                              ...prev,
                              invoiceReportTemplateId: e.target.value,
                            }))
                          }
                          className="w-full min-w-[22rem] px-3 py-2 text-sm border border-xtnd-dark-200 dark:border-xtnd-dark-500 rounded-md focus:outline-none focus:ring-2 focus:ring-xtnd-blue bg-white dark:bg-xtnd-dark-700 text-xtnd-dark dark:text-xtnd-white"
                        >
                          <option value="">Select template...</option>
                          {pdfTemplates.map(template => (
                            <option key={template.id} value={template.id}>
                              {template.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-6 py-4 align-top">
                        <select
                          value={config.ledgerReportTemplateId || ''}
                          onChange={e =>
                            setConfig(prev => ({ ...prev, ledgerReportTemplateId: e.target.value }))
                          }
                          className="w-full min-w-[22rem] px-3 py-2 text-sm border border-xtnd-dark-200 dark:border-xtnd-dark-500 rounded-md focus:outline-none focus:ring-2 focus:ring-xtnd-blue bg-white dark:bg-xtnd-dark-700 text-xtnd-dark dark:text-xtnd-white"
                        >
                          <option value="">Select ledger template...</option>
                          {pdfTemplates.map(template => (
                            <option key={template.id} value={template.id}>
                              {template.name}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>

                    {/* TRUST REQUESTS Section */}
                    <tr className="bg-xtnd-dark-50 dark:bg-xtnd-dark-600">
                      <td
                        colSpan={6}
                        className="px-6 py-3 text-left text-xs font-bold text-xtnd-dark-600 dark:text-xtnd-dark-200 uppercase tracking-wider border-b-2 border-xtnd-dark-200 dark:border-xtnd-dark-400"
                      >
                        TRUST REQUESTS
                      </td>
                    </tr>
                    {/* Final Bill (Legacy Trust Request) */}
                    <tr>
                      <td className="sticky left-0 z-[1] bg-white dark:bg-xtnd-dark-800 px-6 py-4 whitespace-nowrap text-sm font-medium text-xtnd-dark-800 dark:text-xtnd-dark-50">
                        Final Bill
                        <p className="text-xs text-xtnd-dark-400 dark:text-xtnd-dark-300 mt-1">
                          For final bill trust requests (legacy)
                        </p>
                      </td>
                      <td className="px-6 py-4 align-top">
                        <select
                          value={config.trustRequestEmailTemplateId || ''}
                          onChange={e =>
                            setConfig(prev => ({
                              ...prev,
                              trustRequestEmailTemplateId: e.target.value,
                            }))
                          }
                          className={`w-full min-w-[22rem] px-3 py-2 text-sm border border-xtnd-dark-200 dark:border-xtnd-dark-500 rounded-md focus:outline-none focus:ring-2 focus:ring-xtnd-blue bg-white dark:bg-xtnd-dark-700 text-xtnd-dark dark:text-xtnd-white ${validationErrors.trustRequestEmailTemplateId ? 'border-red-500' : ''}`}
                          required
                        >
                          <option value="">Select template...</option>
                          {emailTemplates.map(template => (
                            <option key={template.id} value={template.id}>
                              {template.name}
                            </option>
                          ))}
                        </select>
                        {validationErrors.trustRequestEmailTemplateId && (
                          <p className="text-xs text-red-600 mt-1">
                            {validationErrors.trustRequestEmailTemplateId}
                          </p>
                        )}
                      </td>
                      <td className="px-6 py-4 align-top">
                        <select
                          value={config.trustRequestReminderEmailTemplateId || ''}
                          onChange={e =>
                            setConfig(prev => ({
                              ...prev,
                              trustRequestReminderEmailTemplateId: e.target.value,
                            }))
                          }
                          className="w-full min-w-[22rem] px-3 py-2 text-sm border border-xtnd-dark-200 dark:border-xtnd-dark-500 rounded-md focus:outline-none focus:ring-2 focus:ring-xtnd-blue bg-white dark:bg-xtnd-dark-700 text-xtnd-dark dark:text-xtnd-white"
                        >
                          <option value="">Select template...</option>
                          {emailTemplates.map(template => (
                            <option key={template.id} value={template.id}>
                              {template.name}
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-xtnd-dark-400 dark:text-xtnd-dark-300 mt-1">
                          Optional: Used for payment reminder follow-ups
                        </p>
                      </td>
                      <td className="px-6 py-4">
                        <Input
                          value={config.trustRequestEmailSubject}
                          onChange={e =>
                            setConfig(prev => ({
                              ...prev,
                              trustRequestEmailSubject: e.target.value,
                            }))
                          }
                          placeholder="Trust Replenishment Request - #{trustRequestNumber}"
                          className={`w-full ${validationErrors.trustRequestEmailSubject ? 'border-red-500' : ''}`}
                          required
                        />
                        {validationErrors.trustRequestEmailSubject && (
                          <p className="text-xs text-red-600 mt-1">
                            {validationErrors.trustRequestEmailSubject}
                          </p>
                        )}
                        <p className="text-xs text-xtnd-dark-400 dark:text-xtnd-dark-300 mt-1">
                          Variables: {'{'}trustRequestNumber{'}'}
                        </p>
                      </td>
                      <td className="px-6 py-4 align-top">
                        <select
                          value={config.trustRequestTemplateId || ''}
                          onChange={e =>
                            setConfig(prev => ({ ...prev, trustRequestTemplateId: e.target.value }))
                          }
                          className="w-full min-w-[22rem] px-3 py-2 text-sm border border-xtnd-dark-200 dark:border-xtnd-dark-500 rounded-md focus:outline-none focus:ring-2 focus:ring-xtnd-blue bg-white dark:bg-xtnd-dark-700 text-xtnd-dark dark:text-xtnd-white"
                        >
                          <option value="">Select template...</option>
                          {pdfTemplates.map(template => (
                            <option key={template.id} value={template.id}>
                              {template.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className="text-xtnd-dark-300 dark:text-xtnd-dark-400">N/A</span>
                      </td>
                    </tr>

                    {/* Trust Establishment */}
                    <tr className="bg-xtnd-dark-50 dark:bg-xtnd-dark-700">
                      <td className="sticky left-0 z-[1] bg-xtnd-dark-50 dark:bg-xtnd-dark-700 px-6 py-4 whitespace-nowrap text-sm font-medium text-xtnd-dark-800 dark:text-xtnd-dark-50">
                        Trust Establishment
                        <p className="text-xs text-xtnd-dark-400 dark:text-xtnd-dark-300 mt-1">
                          For initial trust setup requests
                        </p>
                      </td>
                      <td className="px-6 py-4 align-top">
                        <select
                          value={config.trustEstablishmentEmailTemplateId || ''}
                          onChange={e =>
                            setConfig(prev => ({
                              ...prev,
                              trustEstablishmentEmailTemplateId: e.target.value,
                            }))
                          }
                          className={`w-full min-w-[22rem] px-3 py-2 text-sm border border-xtnd-dark-200 dark:border-xtnd-dark-500 rounded-md focus:outline-none focus:ring-2 focus:ring-xtnd-blue bg-white dark:bg-xtnd-dark-700 text-xtnd-dark dark:text-xtnd-white ${validationErrors.trustEstablishmentEmailTemplateId ? 'border-red-500' : ''}`}
                          required
                        >
                          <option value="">Select template...</option>
                          {emailTemplates.map(template => (
                            <option key={template.id} value={template.id}>
                              {template.name}
                            </option>
                          ))}
                        </select>
                        {validationErrors.trustEstablishmentEmailTemplateId && (
                          <p className="text-xs text-red-600 mt-1">
                            {validationErrors.trustEstablishmentEmailTemplateId}
                          </p>
                        )}
                      </td>
                      <td className="px-6 py-4 align-top">
                        <select
                          value={config.trustEstablishmentReminderEmailTemplateId || ''}
                          onChange={e =>
                            setConfig(prev => ({
                              ...prev,
                              trustEstablishmentReminderEmailTemplateId: e.target.value,
                            }))
                          }
                          className="w-full min-w-[22rem] px-3 py-2 text-sm border border-xtnd-dark-200 dark:border-xtnd-dark-500 rounded-md focus:outline-none focus:ring-2 focus:ring-xtnd-blue bg-white dark:bg-xtnd-dark-700 text-xtnd-dark dark:text-xtnd-white"
                        >
                          <option value="">Select template...</option>
                          {emailTemplates.map(template => (
                            <option key={template.id} value={template.id}>
                              {template.name}
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-xtnd-dark-400 dark:text-xtnd-dark-300 mt-1">
                          Optional: Used for payment reminder follow-ups
                        </p>
                      </td>
                      <td className="px-6 py-4">
                        <Input
                          value={config.trustEstablishmentEmailSubject || ''}
                          onChange={e =>
                            setConfig(prev => ({
                              ...prev,
                              trustEstablishmentEmailSubject: e.target.value,
                            }))
                          }
                          placeholder="Trust Establishment Request - #{trustRequestNumber}"
                          className={`w-full ${validationErrors.trustEstablishmentEmailSubject ? 'border-red-500' : ''}`}
                          required
                        />
                        {validationErrors.trustEstablishmentEmailSubject && (
                          <p className="text-xs text-red-600 mt-1">
                            {validationErrors.trustEstablishmentEmailSubject}
                          </p>
                        )}
                        <p className="text-xs text-xtnd-dark-400 dark:text-xtnd-dark-300 mt-1">
                          Variables: {'{'}trustRequestNumber{'}'}
                        </p>
                      </td>
                      <td className="px-6 py-4 align-top">
                        <select
                          value={config.trustRequestTemplateId || ''}
                          onChange={e =>
                            setConfig(prev => ({ ...prev, trustRequestTemplateId: e.target.value }))
                          }
                          className="w-full min-w-[22rem] px-3 py-2 text-sm border border-xtnd-dark-200 dark:border-xtnd-dark-500 rounded-md focus:outline-none focus:ring-2 focus:ring-xtnd-blue bg-white dark:bg-xtnd-dark-700 text-xtnd-dark dark:text-xtnd-white"
                        >
                          <option value="">Select template...</option>
                          {pdfTemplates.map(template => (
                            <option key={template.id} value={template.id}>
                              {template.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className="text-xtnd-dark-300 dark:text-xtnd-dark-400">N/A</span>
                      </td>
                    </tr>

                    {/* Mid-Cycle Request */}
                    <tr>
                      <td className="sticky left-0 z-[1] bg-white dark:bg-xtnd-dark-800 px-6 py-4 whitespace-nowrap text-sm font-medium text-xtnd-dark-800 dark:text-xtnd-dark-50">
                        Mid-Cycle Request
                        <p className="text-xs text-xtnd-dark-400 dark:text-xtnd-dark-300 mt-1">
                          For mid-cycle trust replenishment
                        </p>
                      </td>
                      <td className="px-6 py-4 align-top">
                        <select
                          value={config.midCycleRequestEmailTemplateId || ''}
                          onChange={e =>
                            setConfig(prev => ({
                              ...prev,
                              midCycleRequestEmailTemplateId: e.target.value,
                            }))
                          }
                          className={`w-full min-w-[22rem] px-3 py-2 text-sm border border-xtnd-dark-200 dark:border-xtnd-dark-500 rounded-md focus:outline-none focus:ring-2 focus:ring-xtnd-blue bg-white dark:bg-xtnd-dark-700 text-xtnd-dark dark:text-xtnd-white ${validationErrors.midCycleRequestEmailTemplateId ? 'border-red-500' : ''}`}
                          required
                        >
                          <option value="">Select template...</option>
                          {emailTemplates.map(template => (
                            <option key={template.id} value={template.id}>
                              {template.name}
                            </option>
                          ))}
                        </select>
                        {validationErrors.midCycleRequestEmailTemplateId && (
                          <p className="text-xs text-red-600 mt-1">
                            {validationErrors.midCycleRequestEmailTemplateId}
                          </p>
                        )}
                      </td>
                      <td className="px-6 py-4 align-top">
                        <select
                          value={config.midCycleRequestReminderEmailTemplateId || ''}
                          onChange={e =>
                            setConfig(prev => ({
                              ...prev,
                              midCycleRequestReminderEmailTemplateId: e.target.value,
                            }))
                          }
                          className="w-full min-w-[22rem] px-3 py-2 text-sm border border-xtnd-dark-200 dark:border-xtnd-dark-500 rounded-md focus:outline-none focus:ring-2 focus:ring-xtnd-blue bg-white dark:bg-xtnd-dark-700 text-xtnd-dark dark:text-xtnd-white"
                        >
                          <option value="">Select template...</option>
                          {emailTemplates.map(template => (
                            <option key={template.id} value={template.id}>
                              {template.name}
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-xtnd-dark-400 dark:text-xtnd-dark-300 mt-1">
                          Optional: Used for payment reminder follow-ups
                        </p>
                      </td>
                      <td className="px-6 py-4">
                        <Input
                          value={config.midCycleRequestEmailSubject || ''}
                          onChange={e =>
                            setConfig(prev => ({
                              ...prev,
                              midCycleRequestEmailSubject: e.target.value,
                            }))
                          }
                          placeholder="Mid-Cycle Trust Request - #{trustRequestNumber}"
                          className={`w-full ${validationErrors.midCycleRequestEmailSubject ? 'border-red-500' : ''}`}
                          required
                        />
                        {validationErrors.midCycleRequestEmailSubject && (
                          <p className="text-xs text-red-600 mt-1">
                            {validationErrors.midCycleRequestEmailSubject}
                          </p>
                        )}
                        <p className="text-xs text-xtnd-dark-400 dark:text-xtnd-dark-300 mt-1">
                          Variables: {'{'}trustRequestNumber{'}'}
                        </p>
                      </td>
                      <td className="px-6 py-4 align-top">
                        <select
                          value={config.trustRequestTemplateId || ''}
                          onChange={e =>
                            setConfig(prev => ({ ...prev, trustRequestTemplateId: e.target.value }))
                          }
                          className="w-full min-w-[22rem] px-3 py-2 text-sm border border-xtnd-dark-200 dark:border-xtnd-dark-500 rounded-md focus:outline-none focus:ring-2 focus:ring-xtnd-blue bg-white dark:bg-xtnd-dark-700 text-xtnd-dark dark:text-xtnd-white"
                        >
                          <option value="">Select template...</option>
                          {pdfTemplates.map(template => (
                            <option key={template.id} value={template.id}>
                              {template.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className="text-xtnd-dark-300 dark:text-xtnd-dark-400">N/A</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-xtnd-dark-600 dark:text-xtnd-dark-200">
                  BCC Recipients
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={addBccEmail}
                  leftIcon={<Icon name="HiPlus" size="sm" />}
                >
                  Add BCC
                </Button>
              </div>
              {config.emailBcc.map((email, index) => (
                <div key={index} className="flex items-center space-x-2 mt-2">
                  <Input
                    type="email"
                    value={email}
                    onChange={e => updateBccEmail(index, e.target.value)}
                    placeholder="bcc@yourfirm.com"
                    className={`flex-1 ${validationErrors[`emailBcc_${index}`] ? 'border-red-500' : ''}`}
                  />
                  <Button variant="ghost" size="sm" onClick={() => removeBccEmail(index)}>
                    <Icon name="HiTrash" className="h-4 w-4 text-red-500" />
                  </Button>
                  {validationErrors[`emailBcc_${index}`] && (
                    <p className="text-sm text-red-600">{validationErrors[`emailBcc_${index}`]}</p>
                  )}
                </div>
              ))}
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="autoSendEmails"
                checked={config.autoSendEmails}
                onChange={e => setConfig(prev => ({ ...prev, autoSendEmails: e.target.checked }))}
                className="rounded border-xtnd-dark-200 dark:border-xtnd-dark-500 text-xtnd-blue focus:ring-xtnd-blue dark:bg-xtnd-dark-700 dark:focus:ring-xtnd-blue"
              />
              <Label htmlFor="autoSendEmails">
                Auto-send emails (uncheck to hold for manual release)
              </Label>
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="enableEmailReceipts"
                checked={config.enableEmailReceipts}
                onChange={e =>
                  setConfig(prev => ({ ...prev, enableEmailReceipts: e.target.checked }))
                }
                className="rounded border-xtnd-dark-200 dark:border-xtnd-dark-500 text-xtnd-blue focus:ring-xtnd-blue dark:bg-xtnd-dark-700 dark:focus:ring-xtnd-blue"
              />
              <Label htmlFor="enableEmailReceipts">Enable Email Receipts Processing</Label>
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="includeLedgerInEmail"
                checked={config.includeLedgerInEmail !== false}
                onChange={e =>
                  setConfig(prev => ({ ...prev, includeLedgerInEmail: e.target.checked }))
                }
                className="rounded border-xtnd-dark-200 dark:border-xtnd-dark-500 text-xtnd-blue focus:ring-xtnd-blue dark:bg-xtnd-dark-700 dark:focus:ring-xtnd-blue"
              />
              <Label htmlFor="includeLedgerInEmail">Generate and attach ledger PDF</Label>
            </div>

            {config.includeLedgerInEmail !== false && (
              <div className="flex items-center space-x-2 ml-6">
                <Label htmlFor="ledgerTransactionFilter">Transaction filter:</Label>
                <select
                  id="ledgerTransactionFilter"
                  value={config.ledgerTransactionFilter || 'all'}
                  onChange={e =>
                    setConfig(prev => ({ ...prev, ledgerTransactionFilter: e.target.value }))
                  }
                  className="px-3 py-2 text-sm border border-xtnd-dark-200 dark:border-xtnd-dark-500 rounded-md focus:outline-none focus:ring-2 focus:ring-xtnd-blue bg-white dark:bg-xtnd-dark-700 text-xtnd-dark dark:text-xtnd-white"
                >
                  <option value="all">All transactions</option>
                  <option value="rolling_3">Rolling 3 months</option>
                  <option value="rolling_6">Rolling 6 months</option>
                  <option value="rolling_9">Rolling 9 months</option>
                  <option value="rolling_12">Rolling 12 months</option>
                </select>
              </div>
            )}
          </CardBody>
        </Card>

        {/* Bank and Payment Configuration */}
        <BankAccountCustomFieldMapping
          config={config}
          onConfigChange={updates => setConfig(prev => ({ ...prev, ...updates }))}
        />

        {/* Conditional Logic Configuration */}
        <Card>
          <CardHeader>
            <h2 className="text-xl font-semibold text-xtnd-dark dark:text-xtnd-white">
              Conditional Logic Configurations
            </h2>
            <p className="text-sm text-xtnd-dark-500 dark:text-xtnd-dark-300 mt-1">
              Configure conditional logic to determine processing paths based on matter custom
              fields
            </p>
          </CardHeader>
          <CardBody className="space-y-6">
            {/* Legacy Bill Date Filter */}
            <div className="border-l-4 border-orange-500 pl-4">
              <div className="flex items-start justify-between mb-2">
                <h3 className="text-lg font-medium text-xtnd-dark dark:text-xtnd-white">
                  🕒 Legacy Bill Date Filter
                </h3>
              </div>
              <p className="text-sm text-xtnd-dark-500 dark:text-xtnd-dark-300 mb-4">
                <strong>Hard rule:</strong> Ignore all bills created before this date. This prevents
                processing old bills from before the Juniper release. Leave empty to process all
                bills.
              </p>
              <div className="space-y-2">
                <Label htmlFor="legacyBillCutoffDate">Cutoff Date</Label>
                <Input
                  id="legacyBillCutoffDate"
                  type="date"
                  value={config.legacyBillCutoffDate || ''}
                  onChange={e =>
                    setConfig(prev => ({
                      ...prev,
                      legacyBillCutoffDate: e.target.value || undefined,
                    }))
                  }
                  placeholder="YYYY-MM-DD"
                  className={`w-full max-w-xs ${validationErrors.legacyBillCutoffDate ? 'border-red-500' : ''}`}
                />
                <p className="text-xs text-xtnd-dark-400 dark:text-xtnd-dark-300">
                  Bills created before this date will be logged as "legacy bills" and discarded.
                  Format: YYYY-MM-DD (e.g., 2024-01-15)
                </p>
                {validationErrors.legacyBillCutoffDate && (
                  <p className="text-sm text-red-600">{validationErrors.legacyBillCutoffDate}</p>
                )}
              </div>
            </div>

            {/* Enable Juniper Processing Logic */}
            <div className="border-l-4 border-xtnd-blue pl-4">
              <div className="flex items-start justify-between mb-2">
                <h3 className="text-lg font-medium text-xtnd-dark dark:text-xtnd-white">
                  🚀 Enable Juniper Processing
                </h3>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="invertJuniperProcessingLogic"
                    checked={
                      config.conditionalLogicConfigurations?.invertJuniperProcessingLogic || false
                    }
                    onChange={e =>
                      setConfig(prev => ({
                        ...prev,
                        conditionalLogicConfigurations: {
                          ...prev.conditionalLogicConfigurations,
                          enableJuniperProcessing:
                            prev.conditionalLogicConfigurations?.enableJuniperProcessing || [],
                          invertJuniperProcessingLogic: e.target.checked,
                          evergreenTrustProcessing:
                            prev.conditionalLogicConfigurations?.evergreenTrustProcessing || [],
                        },
                      }))
                    }
                    className="w-4 h-4 text-xtnd-blue focus:ring-xtnd-blue border-xtnd-dark-200 rounded"
                  />
                  <Label htmlFor="invertJuniperProcessingLogic" className="text-sm font-medium">
                    Invert Logic (NOT)
                  </Label>
                </div>
              </div>
              <p className="text-sm text-xtnd-dark-500 dark:text-xtnd-dark-300 mb-4">
                {config.conditionalLogicConfigurations?.invertJuniperProcessingLogic
                  ? '⚠️ When conditions are met, the bill will be IGNORED (excluded from Juniper processing).'
                  : 'When conditions are met, the bill will be processed by Juniper.'}{' '}
                If no conditions are configured, all matters will follow default processing rules.
              </p>
              <ConditionLogicBuilder
                title="Enable Juniper Processing"
                groups={config.conditionalLogicConfigurations?.enableJuniperProcessing || []}
                onChange={groups =>
                  setConfig(prev => ({
                    ...prev,
                    conditionalLogicConfigurations: {
                      ...prev.conditionalLogicConfigurations,
                      enableJuniperProcessing: groups,
                      invertJuniperProcessingLogic:
                        prev.conditionalLogicConfigurations?.invertJuniperProcessingLogic || false,
                      evergreenTrustProcessing:
                        prev.conditionalLogicConfigurations?.evergreenTrustProcessing || [],
                    },
                  }))
                }
                customFields={customFields}
              />
            </div>

            {/* Evergreen Trust Processing Logic */}
            <div className="border-l-4 border-xtnd-dark pl-4">
              <h3 className="text-lg font-medium text-xtnd-dark dark:text-xtnd-white mb-2">
                🏦 Evergreen Trust Processing
              </h3>
              <p className="text-sm text-xtnd-dark-500 dark:text-xtnd-dark-300 mb-4">
                When Juniper processing is enabled, route to Evergreen Trust Request Creation if
                these conditions are met. Otherwise, route to Operating Direct Invoicing. Only
                evaluated if Juniper processing is enabled.
              </p>
              <ConditionLogicBuilder
                title="Evergreen Trust Processing"
                groups={config.conditionalLogicConfigurations?.evergreenTrustProcessing || []}
                onChange={groups =>
                  setConfig(prev => ({
                    ...prev,
                    conditionalLogicConfigurations: {
                      enableJuniperProcessing:
                        prev.conditionalLogicConfigurations?.enableJuniperProcessing || [],
                      invertJuniperProcessingLogic:
                        prev.conditionalLogicConfigurations?.invertJuniperProcessingLogic || false,
                      evergreenTrustProcessing: groups,
                    },
                  }))
                }
                customFields={customFields}
              />
            </div>
          </CardBody>
        </Card>

        {/* Clio Matter Folder Integration Settings */}
        <Card>
          <CardHeader>
            <h2 className="text-xl font-semibold text-xtnd-dark dark:text-xtnd-white">
              Clio Matter Folder Integration
            </h2>
            <p className="text-sm text-xtnd-dark-500 dark:text-xtnd-dark-300 mt-1">
              Configure automatic folder creation and file organization in Clio matters
            </p>
          </CardHeader>
          <CardBody className="space-y-6">
            {/* Folder Structure Display */}
            <div className="bg-xtnd-dark-50 dark:bg-xtnd-dark-700 p-4 rounded-lg">
              <h3 className="text-sm font-medium text-xtnd-dark dark:text-xtnd-white mb-3">
                Folder Structure Preview
              </h3>
              <div className="text-sm font-mono text-xtnd-dark-600 dark:text-xtnd-dark-200 space-y-1">
                <div>📁 Matter Root Folder/</div>
                <div className="ml-4">└── 📁 {config.clioMatterRootFolderName}/</div>
                <div className="ml-8">├── 📁 {config.clioMatterReceiptsFolderName}/</div>
                <div className="ml-12">│ ├── 📁 {config.clioMatterReceiptsOutboxFolderName}/</div>
                <div className="ml-12">│ └── 📁 {config.clioMatterReceiptsFiledFolderName}/</div>
                <div className="ml-16">
                  │ └── 📁 {config.clioMatterReceiptsFiledSubfolderTemplate}/
                </div>
                <div className="ml-8">└── 📁 {config.clioMatterInvoicesFolderName}/</div>
                <div className="ml-12">├── 📄 {config.clioMatterInvoicePdfFilenameTemplate}</div>
                <div className="ml-12">└── 📄 {config.clioMatterLedgerPdfFilenameTemplate}</div>
              </div>
            </div>

            {/* Configurable Templates */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-xtnd-dark dark:text-xtnd-white">
                Configurable Templates
              </h3>

              <div>
                <Label htmlFor="receiptFiledSubfolder">Receipt Filed Subfolder Template *</Label>
                <Input
                  id="receiptFiledSubfolder"
                  value={config.clioMatterReceiptsFiledSubfolderTemplate}
                  onChange={e =>
                    setConfig(prev => ({
                      ...prev,
                      clioMatterReceiptsFiledSubfolderTemplate: e.target.value,
                    }))
                  }
                  placeholder="{juniperId}-{trustRequestNumber}-{operationalInvoiceNumber}"
                  className={`w-full ${validationErrors.clioMatterReceiptsFiledSubfolderTemplate ? 'border-red-500' : ''}`}
                  size="lg"
                />
                {validationErrors.clioMatterReceiptsFiledSubfolderTemplate && (
                  <p className="text-sm text-red-600 mt-1">
                    {validationErrors.clioMatterReceiptsFiledSubfolderTemplate}
                  </p>
                )}
                <p className="text-xs text-xtnd-dark-400 dark:text-xtnd-dark-300 mt-1">
                  Folder name for organizing receipts by transaction. Variables: {'{'}juniperId{'}'}
                  , {'{'}trustRequestNumber{'}'}, {'{'}operationalInvoiceNumber{'}'}
                </p>
              </div>

              <div>
                <Label htmlFor="invoicePdfTemplate">Invoice PDF Filename Template *</Label>
                <Input
                  id="invoicePdfTemplate"
                  value={config.clioMatterInvoicePdfFilenameTemplate}
                  onChange={e =>
                    setConfig(prev => ({
                      ...prev,
                      clioMatterInvoicePdfFilenameTemplate: e.target.value,
                    }))
                  }
                  placeholder="{juniperId}-{trustRequestNumber}-{operationalInvoiceNumber}-Invoice.pdf"
                  className={`w-full ${validationErrors.clioMatterInvoicePdfFilenameTemplate ? 'border-red-500' : ''}`}
                  size="lg"
                />
                {validationErrors.clioMatterInvoicePdfFilenameTemplate && (
                  <p className="text-sm text-red-600 mt-1">
                    {validationErrors.clioMatterInvoicePdfFilenameTemplate}
                  </p>
                )}
                <p className="text-xs text-xtnd-dark-400 dark:text-xtnd-dark-300 mt-1">
                  Filename for generated invoice PDFs. Variables: {'{'}juniperId{'}'}, {'{'}
                  trustRequestNumber{'}'}, {'{'}operationalInvoiceNumber{'}'}
                </p>
              </div>

              <div>
                <Label htmlFor="ledgerPdfTemplate">Ledger PDF Filename Template *</Label>
                <Input
                  id="ledgerPdfTemplate"
                  value={config.clioMatterLedgerPdfFilenameTemplate}
                  onChange={e =>
                    setConfig(prev => ({
                      ...prev,
                      clioMatterLedgerPdfFilenameTemplate: e.target.value,
                    }))
                  }
                  placeholder="{juniperId}-{trustRequestNumber}-{operationalInvoiceNumber}-Ledger.pdf"
                  className={`w-full ${validationErrors.clioMatterLedgerPdfFilenameTemplate ? 'border-red-500' : ''}`}
                  size="lg"
                />
                {validationErrors.clioMatterLedgerPdfFilenameTemplate && (
                  <p className="text-sm text-red-600 mt-1">
                    {validationErrors.clioMatterLedgerPdfFilenameTemplate}
                  </p>
                )}
                <p className="text-xs text-xtnd-dark-400 dark:text-xtnd-dark-300 mt-1">
                  Filename for generated ledger PDFs. Variables: {'{'}juniperId{'}'}, {'{'}
                  trustRequestNumber{'}'}, {'{'}operationalInvoiceNumber{'}'}
                </p>
              </div>
            </div>

            {/* Test Configuration */}
            <div className="space-y-4 border-t pt-4 dark:border-xtnd-dark-600">
              <h3 className="text-lg font-medium text-xtnd-dark dark:text-xtnd-white">
                Folder Creation
              </h3>
              <FolderCreationTest onTestFolder={handleTestFolderCreation} />
            </div>

            {/* Information */}
            <div className="bg-blue-50 dark:bg-xtnd-dark-600 p-4 rounded-lg border border-blue-200 dark:border-xtnd-dark-500">
              <div className="flex items-start space-x-3">
                <Icon
                  name="HiInformationCircle"
                  className="h-5 w-5 text-blue-600 dark:text-xtnd-blue mt-0.5"
                />
                <div>
                  <h4 className="font-medium text-blue-900 dark:text-xtnd-white">
                    Folder Structure Notes
                  </h4>
                  <ul className="text-sm text-blue-800 dark:text-xtnd-dark-200 mt-2 space-y-1">
                    <li>• Folders marked with 📁 are automatically created as needed</li>
                    <li>• Fixed folder names (xtnd, juniper-receipts, etc.) cannot be modified</li>
                    <li>• Only the templates above can be customized with variables</li>
                    <li>
                      • Receipts in the Outbox folder are automatically included in the next invoice
                      email
                    </li>
                    <li>• Filed receipts are organized by transaction in subfolders</li>
                  </ul>
                </div>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Trust Request Note Templates */}
        <Card>
          <CardHeader>
            <h2 className="text-xl font-semibold text-xtnd-dark dark:text-xtnd-white">
              Trust Request Note Templates
            </h2>
          </CardHeader>
          <CardBody className="space-y-6">
            <p className="text-sm text-xtnd-dark-400 dark:text-xtnd-dark-300">
              Configure the note text that appears on trust requests in Clio. Use {'{{variable}}'}{' '}
              placeholders for dynamic values. A reference ID is always appended automatically at
              the end.
            </p>

            {/* Operational Trust Request Note */}
            <div>
              <Label htmlFor="operationalTrustRequestNoteTemplate">
                Operational Trust Request Note
              </Label>
              <textarea
                id="operationalTrustRequestNoteTemplate"
                value={config.operationalTrustRequestNoteTemplate}
                onChange={e =>
                  setConfig(prev => ({
                    ...prev,
                    operationalTrustRequestNoteTemplate: e.target.value,
                  }))
                }
                placeholder="{{operationalMemo}}Generated by XTND-Juniper for operational invoice {{operationalInvoiceNumber}}."
                className="w-full mt-1 px-3 py-2 rounded-md border border-xtnd-dark-200 dark:border-xtnd-dark-500 bg-white dark:bg-xtnd-dark-700 text-xtnd-dark dark:text-xtnd-white font-mono text-sm"
                rows={3}
              />
              <p className="text-xs text-xtnd-dark-400 dark:text-xtnd-dark-300 mt-1">
                Available variables:{' '}
                <code className="bg-xtnd-dark-100 dark:bg-xtnd-dark-600 px-1 rounded">
                  {'{{operationalMemo}}'}
                </code>{' '}
                <code className="bg-xtnd-dark-100 dark:bg-xtnd-dark-600 px-1 rounded">
                  {'{{operationalInvoiceNumber}}'}
                </code>{' '}
                <code className="bg-xtnd-dark-100 dark:bg-xtnd-dark-600 px-1 rounded">
                  {'{{juniperId}}'}
                </code>
              </p>
              <p className="text-xs text-blue-600 dark:text-blue-400 mt-1 font-medium">
                Auto-appended: ref: {'{{juniperId}}'}
              </p>
            </div>

            {/* Bulk/Adhoc Trust Request Note */}
            <div>
              <Label htmlFor="bulkTrustRequestNoteTemplate">Bulk / Adhoc Trust Request Note</Label>
              <textarea
                id="bulkTrustRequestNoteTemplate"
                value={config.bulkTrustRequestNoteTemplate}
                onChange={e =>
                  setConfig(prev => ({
                    ...prev,
                    bulkTrustRequestNoteTemplate: e.target.value,
                  }))
                }
                placeholder="{{userNotes}}Bulk trust request."
                className="w-full mt-1 px-3 py-2 rounded-md border border-xtnd-dark-200 dark:border-xtnd-dark-500 bg-white dark:bg-xtnd-dark-700 text-xtnd-dark dark:text-xtnd-white font-mono text-sm"
                rows={3}
              />
              <p className="text-xs text-xtnd-dark-400 dark:text-xtnd-dark-300 mt-1">
                Available variables:{' '}
                <code className="bg-xtnd-dark-100 dark:bg-xtnd-dark-600 px-1 rounded">
                  {'{{userNotes}}'}
                </code>{' '}
                <code className="bg-xtnd-dark-100 dark:bg-xtnd-dark-600 px-1 rounded">
                  {'{{trrId}}'}
                </code>{' '}
                <code className="bg-xtnd-dark-100 dark:bg-xtnd-dark-600 px-1 rounded">
                  {'{{matterId}}'}
                </code>
              </p>
              <p className="text-xs text-blue-600 dark:text-blue-400 mt-1 font-medium">
                Auto-appended: ref: {'{{trrId}}'}
              </p>
            </div>

            {/* Live Preview */}
            <div className="bg-xtnd-dark-50 dark:bg-xtnd-dark-600 p-4 rounded-lg border border-xtnd-dark-200 dark:border-xtnd-dark-500">
              <h4 className="font-medium text-xtnd-dark dark:text-xtnd-white mb-2 text-sm">
                Preview
              </h4>
              <div className="text-sm font-mono text-xtnd-dark-600 dark:text-xtnd-dark-200 space-y-3">
                <div>
                  <span className="text-xs font-semibold uppercase tracking-wide">Operational</span>
                  <pre className="mt-1 whitespace-pre-wrap text-xs bg-white dark:bg-xtnd-dark-700 p-2 rounded border border-xtnd-dark-200 dark:border-xtnd-dark-500">
                    {config.operationalTrustRequestNoteTemplate
                      .replace('{{operationalMemo}}', 'Payment for legal services ')
                      .replace('{{operationalInvoiceNumber}}', '12345')
                      .replace('{{juniperId}}', 'JUN-abc123-def456')}
                    {' ref: JUN-abc123-def456'}
                  </pre>
                </div>
                <div>
                  <span className="text-xs font-semibold uppercase tracking-wide">
                    Bulk / Adhoc
                  </span>
                  <pre className="mt-1 whitespace-pre-wrap text-xs bg-white dark:bg-xtnd-dark-700 p-2 rounded border border-xtnd-dark-200 dark:border-xtnd-dark-500">
                    {config.bulkTrustRequestNoteTemplate
                      .replace('{{userNotes}}', 'Quarterly trust replenishment ')
                      .replace('{{trrId}}', 'TRR-98765')
                      .replace('{{matterId}}', '54321')}
                    {' ref: TRR-98765'}
                  </pre>
                </div>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Custom Field Filters Configuration */}
        <Card>
          <CardHeader>
            <h2 className="text-xl font-semibold text-xtnd-dark dark:text-xtnd-white">
              Custom Field Filters
            </h2>
            <p className="text-sm text-xtnd-dark-400 dark:text-xtnd-dark-300 mt-1">
              Enable matter custom fields as filters on the Transactions page. Only picklist, dropdown, and checkbox fields are supported.
            </p>
          </CardHeader>
          <CardBody className="space-y-4">
            {(() => {
              // Filter to only Matter fields with supported types, sorted alphabetically
              const filterableFields = customFields
                .filter(
                  (cf: CustomFieldOption) =>
                    cf.parent_type === 'Matter' &&
                    ['picklist', 'dropdown', 'checkbox'].includes(cf.field_type)
                )
                .sort((a: CustomFieldOption, b: CustomFieldOption) =>
                  a.name.localeCompare(b.name)
                );

              if (filterableFields.length === 0) {
                return (
                  <div className="text-sm text-xtnd-dark-400 dark:text-xtnd-dark-300 py-4 text-center">
                    No picklist, dropdown, or checkbox matter custom fields found. Custom fields are synced from Clio.
                  </div>
                );
              }

              return (
                <div className="space-y-2">
                  <div className="grid grid-cols-[auto_1fr_auto] gap-x-4 gap-y-2 items-center">
                    <div className="text-xs font-medium text-xtnd-dark-400 dark:text-xtnd-dark-300 uppercase tracking-wider">
                      Enabled
                    </div>
                    <div className="text-xs font-medium text-xtnd-dark-400 dark:text-xtnd-dark-300 uppercase tracking-wider">
                      Field Name
                    </div>
                    <div className="text-xs font-medium text-xtnd-dark-400 dark:text-xtnd-dark-300 uppercase tracking-wider">
                      Type
                    </div>
                    {filterableFields.map((field: CustomFieldOption) => {
                      const isEnabled = config.customFieldFilters?.some(
                        (f) => f.fieldId === field.id && f.enabled
                      );
                      return (
                        <React.Fragment key={field.id}>
                          <div className="flex items-center justify-center">
                            <input
                              type="checkbox"
                              checked={isEnabled || false}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setConfig((prev) => {
                                  const existing = prev.customFieldFilters || [];
                                  const idx = existing.findIndex(
                                    (f) => f.fieldId === field.id
                                  );
                                  if (idx >= 0) {
                                    const updated = [...existing];
                                    updated[idx] = { ...updated[idx], enabled: checked };
                                    return { ...prev, customFieldFilters: updated };
                                  }
                                  return {
                                    ...prev,
                                    customFieldFilters: [
                                      ...existing,
                                      {
                                        fieldId: field.id,
                                        fieldName: field.name,
                                        fieldType: field.field_type as 'picklist' | 'dropdown' | 'checkbox',
                                        parentType: 'Matter' as const,
                                        enabled: checked,
                                      },
                                    ],
                                  };
                                });
                              }}
                              className="rounded border-xtnd-dark-200 dark:border-xtnd-dark-500 text-xtnd-blue focus:ring-xtnd-blue dark:bg-xtnd-dark-700"
                            />
                          </div>
                          <div className="text-sm text-xtnd-dark dark:text-xtnd-white">
                            {field.name}
                          </div>
                          <div className="text-xs text-xtnd-dark-400 dark:text-xtnd-dark-300 capitalize">
                            {field.field_type}
                          </div>
                        </React.Fragment>
                      );
                    })}
                  </div>
                  {config.customFieldFilters?.some((f) => f.enabled) && (
                    <div className="mt-3 p-2 bg-xtnd-blue/5 dark:bg-xtnd-blue/10 rounded text-xs text-xtnd-dark-500 dark:text-xtnd-dark-300">
                      {config.customFieldFilters.filter((f) => f.enabled).length} field
                      {config.customFieldFilters.filter((f) => f.enabled).length !== 1
                        ? 's'
                        : ''}{' '}
                      enabled as transaction filters.
                    </div>
                  )}
                </div>
              );
            })()}
          </CardBody>
        </Card>

        {/* Advanced Settings */}
        <Card>
          <CardHeader>
            <h2 className="text-xl font-semibold text-xtnd-dark dark:text-xtnd-white">
              Advanced Settings
            </h2>
          </CardHeader>
          <CardBody className="space-y-4">
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="enableRetry"
                  checked={config.enableTrustRequestRetry}
                  onChange={e =>
                    setConfig(prev => ({ ...prev, enableTrustRequestRetry: e.target.checked }))
                  }
                  className="rounded border-xtnd-dark-200 dark:border-xtnd-dark-500 text-xtnd-blue focus:ring-xtnd-blue dark:bg-xtnd-dark-700 dark:focus:ring-xtnd-blue"
                />
                <Label htmlFor="enableRetry">Enable Trust Request Retry</Label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="enableVoid"
                  checked={config.enableTrustRequestVoid}
                  onChange={e =>
                    setConfig(prev => ({ ...prev, enableTrustRequestVoid: e.target.checked }))
                  }
                  className="rounded border-xtnd-dark-200 dark:border-xtnd-dark-500 text-xtnd-blue focus:ring-xtnd-blue dark:bg-xtnd-dark-700 dark:focus:ring-xtnd-blue"
                />
                <Label htmlFor="enableVoid">Enable Trust Request Void Prior Logic</Label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="autoApproveTrustRequests"
                  checked={config.autoApproveTrustRequests}
                  onChange={e =>
                    setConfig(prev => ({ ...prev, autoApproveTrustRequests: e.target.checked }))
                  }
                  className="rounded border-xtnd-dark-200 dark:border-xtnd-dark-500 text-xtnd-blue focus:ring-xtnd-blue dark:bg-xtnd-dark-700 dark:focus:ring-xtnd-blue"
                />
                <Label htmlFor="autoApproveTrustRequests">Auto-approve Trust Requests</Label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="uploadPaymentReminderRevisionsToClio"
                  checked={config.uploadPaymentReminderRevisionsToClio}
                  onChange={e =>
                    setConfig(prev => ({
                      ...prev,
                      uploadPaymentReminderRevisionsToClio: e.target.checked,
                    }))
                  }
                  className="rounded border-xtnd-dark-200 dark:border-xtnd-dark-500 text-xtnd-blue focus:ring-xtnd-blue dark:bg-xtnd-dark-700 dark:focus:ring-xtnd-blue"
                />
                <Label htmlFor="uploadPaymentReminderRevisionsToClio">
                  Upload Payment Reminder Revisions to Clio
                </Label>
              </div>

              {/* Email Override Section */}
              <div className="border-t pt-4 mt-4">
                <div className="flex items-center space-x-2 mb-3">
                  <input
                    type="checkbox"
                    id="overrideClientEmails"
                    checked={config.overrideClientEmails}
                    onChange={e =>
                      setConfig(prev => ({ ...prev, overrideClientEmails: e.target.checked }))
                    }
                    className="rounded border-xtnd-dark-200 dark:border-xtnd-dark-500 text-xtnd-blue focus:ring-xtnd-blue dark:bg-xtnd-dark-700 dark:focus:ring-xtnd-blue"
                  />
                  <Label htmlFor="overrideClientEmails" className="font-medium">
                    Override All Client Email Traffic
                  </Label>
                  <div className="group relative">
                    <Icon
                      name="HiInformationCircle"
                      className="h-4 w-4 text-xtnd-dark-300 cursor-help"
                    />
                    <div className="absolute left-0 top-6 z-10 w-80 p-3 bg-xtnd-dark-800 text-white text-xs rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                      <strong>Development/Testing Feature:</strong>
                      <br />
                      When enabled, ALL invoice emails will be sent ONLY to the override email
                      address below (plus any BCC recipients). Client email addresses will be
                      completely ignored. Use this for testing without sending emails to real
                      clients.
                    </div>
                  </div>
                </div>

                {config.overrideClientEmails && (
                  <div className="ml-6 space-y-2">
                    <div>
                      <Label htmlFor="overrideEmailAddress" className="text-sm">
                        Override Email Address
                      </Label>
                      <div className="flex items-center space-x-2 mt-1">
                        <Input
                          type="email"
                          id="overrideEmailAddress"
                          value={config.overrideEmailAddress}
                          onChange={e =>
                            setConfig(prev => ({ ...prev, overrideEmailAddress: e.target.value }))
                          }
                          placeholder="test@yourfirm.com"
                          className={`flex-1 ${validationErrors['overrideEmailAddress'] ? 'border-red-500' : ''}`}
                        />
                      </div>
                      {validationErrors['overrideEmailAddress'] && (
                        <p className="text-sm text-red-600 mt-1">
                          {validationErrors['overrideEmailAddress']}
                        </p>
                      )}
                    </div>
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded-md">
                      <div className="flex items-start space-x-2">
                        <Icon
                          name="HiInformationCircle"
                          className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0"
                        />
                        <div>
                          <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                            Email Override Active
                          </p>
                          <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                            All invoice emails will be sent to{' '}
                            <strong>{config.overrideEmailAddress || 'the address above'}</strong>{' '}
                            instead of client email addresses.
                            {config.emailBcc.length > 0 && (
                              <span>
                                {' '}
                                BCC recipients ({config.emailBcc.join(', ')}) will still receive
                                copies.
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-xtnd-dark-600 dark:text-xtnd-dark-200">
                  Escalation Email Recipients
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleAddEscalationEmail}
                  leftIcon={<Icon name="HiPlus" size="sm" />}
                >
                  Add Email
                </Button>
              </div>
              {config.escalationEmails.map((emailEntry, index) => (
                <div key={index} className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Input
                      type="email"
                      value={emailEntry.email}
                      onChange={e => handleUpdateEscalationEmail(index, e.target.value)}
                      placeholder="ops@yourfirm.com"
                      className={`flex-1 ${validationErrors[`escalationEmail[${index}]`] ? 'border-red-500' : ''}`}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveEscalationEmail(index)}
                    >
                      <Icon name="HiTrash" className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                  {validationErrors[`escalationEmail[${index}]`] && (
                    <p className="text-sm text-red-600">
                      {validationErrors[`escalationEmail[${index}]`]}
                    </p>
                  )}
                  {/* Verification Status Message */}
                  {emailEntry.email &&
                    emailEntry.verificationStatus &&
                    emailEntry.verificationStatus !== 'not_started' && (
                      <div className="ml-8 text-xs">
                        {emailEntry.verificationStatus === 'verified' && (
                          <span className="text-green-600 dark:text-green-400">
                            ✅ Verified - Can receive escalation emails
                          </span>
                        )}
                        {emailEntry.verificationStatus === 'pending_verification' && (
                          <span className="text-yellow-600 dark:text-yellow-400">
                            ⏳ Pending - Check your email for verification link
                          </span>
                        )}
                        {emailEntry.verificationStatus === 'failed' && (
                          <span className="text-red-600 dark:text-red-400">
                            ❌ Verification failed - Try again
                          </span>
                        )}
                        {emailEntry.lastChecked && (
                          <span className="text-xtnd-dark-400 dark:text-xtnd-dark-300 ml-2">
                            (Last checked: {new Date(emailEntry.lastChecked).toLocaleTimeString()})
                          </span>
                        )}
                      </div>
                    )}
                </div>
              ))}
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Email Receipts Configuration Card */}
      {config.enableEmailReceipts && (
        <Card>
          <CardHeader>
            <div className="flex items-center space-x-2">
              <Icon name="HiDocumentText" className="h-5 w-5 text-xtnd-blue" />
              <h2 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-light">
                Email Receipts Configuration
              </h2>
            </div>
            <p className="text-sm text-xtnd-dark-500 dark:text-xtnd-dark-400 mt-1">
              Configure email receipts processing for trust request transactions
            </p>
          </CardHeader>
          <CardBody className="space-y-4">
            <div>
              <Label htmlFor="maxFilesPerEmail">
                Maximum Files Per Email
                <span className="text-xs text-xtnd-dark-500 dark:text-xtnd-dark-400 ml-2">
                  Maximum number of files to attach to a single email
                </span>
              </Label>
              <Input
                id="maxFilesPerEmail"
                type="number"
                min="1"
                max="50"
                value={config.emailReceiptsConfig.maxFilesPerEmail}
                placeholder="10"
                readOnly
                className="bg-xtnd-dark-50 dark:bg-xtnd-dark-700 cursor-not-allowed"
              />
            </div>

            <div>
              <Label htmlFor="maxTotalSizeBytes">
                Maximum Total Size (MB)
                <span className="text-xs text-xtnd-dark-500 dark:text-xtnd-dark-400 ml-2">
                  Maximum total size for all attachments
                </span>
              </Label>
              <Input
                id="maxTotalSizeBytes"
                type="number"
                min="1"
                max="25"
                value={Math.round(config.emailReceiptsConfig.maxTotalSizeBytes / (1024 * 1024))}
                placeholder="25"
                readOnly
                className="bg-xtnd-dark-50 dark:bg-xtnd-dark-700 cursor-not-allowed"
              />
            </div>

            <div>
              <Label htmlFor="outboxFolderName">
                Outbox Folder Name
                <span className="text-xs text-xtnd-dark-500 dark:text-xtnd-dark-400 ml-2">
                  Folder name for documents to be processed
                </span>
              </Label>
              <Input
                id="outboxFolderName"
                value={config.emailReceiptsConfig.outboxFolderName}
                placeholder="juniper-receipt-outbox"
                readOnly
                className="bg-xtnd-dark-50 dark:bg-xtnd-dark-700 cursor-not-allowed"
              />
            </div>

            <div>
              <Label htmlFor="filedFolderName">
                Filed Folder Name
                <span className="text-xs text-xtnd-dark-500 dark:text-xtnd-dark-400 ml-2">
                  Folder name for processed documents
                </span>
              </Label>
              <Input
                id="filedFolderName"
                value={config.emailReceiptsConfig.filedFolderName}
                placeholder="juniper-receipt-filed"
                readOnly
                className="bg-xtnd-dark-50 dark:bg-xtnd-dark-700 cursor-not-allowed"
              />
            </div>

            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="enableValidation"
                  checked={config.emailReceiptsConfig.enableValidation}
                  disabled
                  className="rounded border-xtnd-dark-200 dark:border-xtnd-dark-500 text-xtnd-blue focus:ring-xtnd-blue dark:bg-xtnd-dark-700 dark:focus:ring-xtnd-blue opacity-50 cursor-not-allowed"
                />
                <Label htmlFor="enableValidation">Enable File Validation</Label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="enableCleanup"
                  checked={config.emailReceiptsConfig.enableCleanup}
                  disabled
                  className="rounded border-xtnd-dark-200 dark:border-xtnd-dark-500 text-xtnd-blue focus:ring-xtnd-blue dark:bg-xtnd-dark-700 dark:focus:ring-xtnd-blue opacity-50 cursor-not-allowed"
                />
                <Label htmlFor="enableCleanup">Enable Cleanup of Sending Folder</Label>
              </div>
            </div>

            <div className="bg-blue-50 dark:bg-xtnd-dark-700 p-3 rounded-md">
              <p className="text-sm text-blue-800 dark:text-xtnd-dark-200">
                <strong>Note:</strong> Allowed file types and size limits are automatically
                configured from your tenant's email service configuration.
              </p>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Juniper Query Settings Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center space-x-2">
            <Icon name="HiClock" className="h-5 w-5 text-xtnd-blue" />
            <h2 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-light">
              Juniper Query Settings
            </h2>
          </div>
          <p className="text-sm text-xtnd-dark-500 dark:text-xtnd-dark-400 mt-1">
            Configure caching and performance settings for live data queries
          </p>
        </CardHeader>
        <CardBody className="space-y-4">
          <div>
            <Label htmlFor="queryCacheTtl">
              Cache TTL (Minutes) *
              <span className="text-xs text-xtnd-dark-500 dark:text-xtnd-dark-400 ml-2">
                How long to cache live Clio data (1-60 minutes)
              </span>
            </Label>
            <Input
              id="queryCacheTtl"
              type="number"
              min="1"
              max="60"
              value={config.queryCacheTtlMinutes}
              onChange={e =>
                setConfig(prev => ({
                  ...prev,
                  queryCacheTtlMinutes: parseInt(e.target.value) || 15,
                }))
              }
              placeholder="15"
              className={validationErrors.queryCacheTtlMinutes ? 'border-red-500' : ''}
            />
            {validationErrors.queryCacheTtlMinutes && (
              <p className="text-sm text-red-600 mt-1">{validationErrors.queryCacheTtlMinutes}</p>
            )}
            <p className="text-xs text-xtnd-dark-500 dark:text-xtnd-dark-400 mt-1">
              Lower values = More live data, higher API usage. Higher values = Better performance,
              less current data.
            </p>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
